"use client";

import { ArrowRight, Calendar, Mail, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const contactMethods = [{
  icon: Mail,
  label: "DIRECT INQUIRY",
  value: "sara@seniorlivinginsights.com"
}, {
  icon: MapPin,
  label: "HEADQUARTERS",
  value: "Minnesota"
}, {
  icon: Calendar,
  label: "SCHEDULE CONSULTATION",
  value: "+763-300-1924"
}];
function ConsultationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 border border-border rounded-lg bg-card">
        <p className="font-mono text-xs uppercase tracking-widest text-primary mb-3">
          REQUEST RECEIVED
        </p>
        <p className="font-default text-lg text-foreground mb-6">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="font-mono text-xs uppercase tracking-widest">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
                YOUR NAME
              </FormLabel>
              <FormControl>
                <Input placeholder="Jane Smith" className="bg-transparent border-0 border-b border-border px-0 h-11 focus-visible:ring-0 focus-visible:border-primary text-foreground placeholder:text-muted-foreground/50" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
                EMAIL
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@example.com" className="bg-transparent border-0 border-b border-border px-0 h-11 focus-visible:ring-0 focus-visible:border-primary text-foreground placeholder:text-muted-foreground/50" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
                PHONE
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (555) 000-0000" className="bg-transparent border-0 border-b border-border px-0 h-11 focus-visible:ring-0 focus-visible:border-primary text-foreground placeholder:text-muted-foreground/50" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="city" rules={{
        required: "City is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
                CITY
              </FormLabel>
              <FormControl>
                <Input placeholder="Enter your city" className="bg-transparent border-0 border-b border-border px-0 h-11 focus-visible:ring-0 focus-visible:border-primary text-foreground placeholder:text-muted-foreground/50" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 border border-destructive/30 bg-destructive/10 rounded-md">
          <p className="text-destructive text-sm">{submitError}</p>
        </div>}

      <div className="pt-4">
        <Button type="submit" disabled={isSubmitting} className="w-full h-14 bg-primary text-primary-foreground hover:bg-primary/90 font-mono text-xs uppercase tracking-widest shadow-md transition-shadow">
          {isSubmitting ? "SENDING..." : "REQUEST CONSULTATION"}
          <ArrowRight className="ml-2 size-4" />
        </Button>
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-4 text-center">
          BY SUBMITTING, YOU AGREE TO OUR PRIVACY AND CARE PROTOCOLS.
        </p>
      </div>
    </Form>;
}
export default function ConnectWithOurTeam() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="4567" id="connect-with-our-team" className="bg-background text-foreground py-24 md:py-32 lg:py-40 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,hsla(83,23%,53%,0.06),transparent_60%)] pointer-events-none" />

      <div className="max-w-7xl mx-auto relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div ref={leftRef} className={`lg:col-span-5 transition-all duration-700 ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <p className="font-mono text-xs uppercase tracking-widest text-primary mb-8">
              01 / INITIATE CARE
            </p>

            <h2 className="font-default text-4xl sm:text-5xl lg:text-6xl font-medium text-foreground leading-[1.05] tracking-tight mb-8">
              Let's simplify your medication management.
            </h2>

            <p className="font-default text-xl leading-relaxed text-muted-foreground mb-12 max-w-md">
              AthenaCare provides premium medication management solutions. Reach out to discuss how our MedaCube technology can eliminate pill sorting and provide peace of mind for your loved ones.
            </p>

            <div className="space-y-4 mb-10">
              {contactMethods.map((method, i) => {
              const Icon = method.icon;
              return <div key={i} data-index={i} className="flex items-start gap-5 p-5 border border-border rounded-lg hover:border-primary/40 transition-colors group">
                    <div className="shrink-0 mt-0.5">
                      <Icon className="size-5 text-primary stroke-[1.5]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground mb-1.5">
                        {method.label}
                      </p>
                      <p className="font-default text-[15px] text-foreground break-words">
                        {method.value}
                      </p>
                    </div>
                  </div>;
            })}
            </div>

            <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground/70 flex items-center gap-2">
              <span className="inline-block size-1.5 rounded-lg bg-primary animate-pulse" />
              Care team response within 24 hours.
            </p>
          </div>

          <div ref={rightRef} className={`lg:col-span-7 transition-all duration-700 delay-150 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="relative">
              <div className="absolute -inset-px bg-gradient-to-b from-transparent via-transparent to-primary/20 rounded-lg blur-sm pointer-events-none" />

              <div className="relative bg-card border border-border rounded-lg p-8 sm:p-12 lg:p-16 shadow-lg">
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-3/4 h-px bg-gradient-to-r from-transparent via-primary/60 to-transparent" />

                <div className="mb-10">
                  <span className="inline-block font-mono text-[11px] uppercase tracking-widest text-primary border border-primary/30 px-3 py-1.5 rounded-lg">
                    CARE INQUIRY / 2025
                  </span>
                </div>

                <FormProvider formKey="connect_form_contact" formId="connect_form_contact" sectionName="connect-with-our-team" defaultValues={{
                name: "",
                email: "",
                phone: "",
                city: ""
              }} validationRules={{
                name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                phone: {
                  required: true,
                  pattern: "phone"
                },
                city: {
                  required: true
                }
              }} submitText="Request Consultation" successMessage="Thank you. A care specialist will be in touch within 24 hours to discuss your needs." errorMessage="We couldn't process your request. Please try again or email us directly.">
                  <ConsultationForm />
                </FormProvider>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}